import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import { memo } from "react";

import { ChildToggleButton } from "@/components/ChildToggleButton";
import { errorTextClass } from "@/components/ErrorSpan";
import { TruncatedText } from "@/components/TruncatedText";
import { PurchasingPriorityCell } from "@/features/purchasing-priority/PurchasingPriorityCell";
import { formatDineroCad, toNumber } from "@/utils/dinero-util";
import { hasBom } from "@/utils/sos/utilities";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Job } from "../../queries/job-by-name";
import { BomNode } from "../../queries/tracking-data/bom-hierarchy-generator";
import { CostTaxBreakdown } from "../../queries/tracking-data/cost-tax-breakdown";
import { CostTaxBreakdownErrorsPopover } from "../CostTaxBreakdownErrorsPopover";
import { LeadTimeCell } from "../LeadTimeCell";
import { PreferredVendorCell } from "../PreferredVendorCell";

export interface BomHierarchyTableRow extends Omit<BomNode, "children"> {
  unitPurchaseCost: CostTaxBreakdown;
  totalPurchaseCost: CostTaxBreakdown;
  unitLabourCost: CostTaxBreakdown;
  totalLabourCost: CostTaxBreakdown;
  children: BomHierarchyTableRow[];
}

const columnHelper = createColumnHelper<BomHierarchyTableRow>();

export const bomHierarchyTableColumns = [
  columnHelper.accessor("item.name", {
    header: "Item",
    meta: { cellProps: { className: "tree-shaded" } },
    cell: ({ getValue, row: { original: node, getIsExpanded, getToggleExpandedHandler } }) => (
      <pre className="m-0">
        <span className="lh-1" style={{ fontSize: "calc(var(--bs-body-line-height) * 1em)" }}>
          {node.asciiTreePrefix}
        </span>
        {getValue()}{" "}
        <ChildToggleButton showChildren={getIsExpanded()} toggleShowChildren={getToggleExpandedHandler()}>
          {hasBom(node.item.type) ? node.children : null}
        </ChildToggleButton>
      </pre>
    ),
  }),

  // This column will take up all remaining horizontal space in the table. The contents are positioned absolutely
  // so that the text can be truncated without affecting the layout of the table.
  columnHelper.accessor("item.description", {
    header: "Description",
    meta: { cellProps: { className: "tree-shaded position-relative w-100", style: { minWidth: 300 } } },
    cell: ({ getValue }) => <MemoizedDescriptionCell text={getValue()} />,
  }),

  // The text in this column is allowed to wrap. This isn't ideal but notes are quite rare so it's not a big deal
  columnHelper.accessor("notes", {
    header: "Notes",
    meta: { cellProps: { className: "tree-shaded text-wrap", style: { minWidth: 150 } } },
  }),

  // This column has a max width and will be truncated if the vendor name is too long
  columnHelper.accessor("item.preferredVendor.name", {
    header: "Preferred Vendor",
    meta: { cellProps: { className: "tree-shaded", style: { maxWidth: 150 } } },
    cell: ({ row }) => <MemoizedTruncatedPreferredVendorCell item={row.original.item} />,
  }),

  columnHelper.accessor("quantity", {
    header: "Qty.",
    meta: { cellProps: { className: "tree-shaded" } },
    cell: ({ getValue }) => getValue().toLocaleString(undefined, { maximumFractionDigits: 5 }),
  }),

  columnHelper.accessor("item.leadTimeMs", {
    header: "Lead Time",
    meta: { cellProps: { className: "tree-shaded" } },
    cell: ({ row }) => <LeadTimeCell item={row.original.item} />,
  }),

  columnHelper.accessor("totalPurchaseCost.total", {
    header: "Estimated Purchase Costs",
    meta: {
      helpText: "NOTE: Does NOT include labour",
      cellProps: { className: "tree-shaded" },
    },
    cell: ({ row: { original } }) => {
      // Don't show purchase costs for labor items
      if (original.item.type === "Labor") return null;

      // Show a list of errors if the cost calculation failed
      const totalPurchaseCost = original.totalPurchaseCost;
      if (totalPurchaseCost.errors.length) return <CostTaxBreakdownErrorsPopover cost={totalPurchaseCost} />;

      // Show a tooltip with the breakdown of the cost
      const formattedTotal = formatDineroCad(totalPurchaseCost.total);
      return (
        <OverlayTrigger
          trigger={["hover", "focus"]}
          overlay={(props) => (
            <Tooltip {...props}>
              {formatDineroCad(original.unitPurchaseCost.total)} x{" "}
              {original.totalQuantity.toLocaleString(undefined, { maximumFractionDigits: 5 })} = {formattedTotal}
            </Tooltip>
          )}
        >
          <span>{formattedTotal}</span>
        </OverlayTrigger>
      );
    },
  }),

  columnHelper.accessor("totalLabourCost.total", {
    header: "Estimated Labour",
    meta: { cellProps: { className: "tree-shaded" } },
    cell: ({ row: { original } }) => {
      // Don't show labour cost for leaf nodes (always zero)
      if (!original.children.length) return null;

      // Calculate the total number of hours based on the labour cost
      // NOTE: Assumes a rate of $100/hr for all labour
      const totalLabourCost = original.totalLabourCost;
      const totalHours = toNumber(totalLabourCost.subtotal) / 100;

      // Don't show labour cost for assemblies built via contract manufacturing
      if (!totalHours && original.children.some((x) => x.item.name.endsWith("-CM"))) return null;

      // Show a list of errors if the cost calculation failed
      if (totalLabourCost.errors.length) return <CostTaxBreakdownErrorsPopover cost={totalLabourCost} />;

      // Show a tooltip with the breakdown of the cost
      const formattedTotal = totalHours.toLocaleString(undefined, { maximumFractionDigits: 2 }) + " hrs";
      return (
        <OverlayTrigger
          trigger={["hover", "focus"]}
          overlay={(props) => (
            <Tooltip {...props}>
              {(toNumber(original.unitLabourCost.subtotal) / 100).toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}{" "}
              hrs x {original.totalQuantity.toLocaleString(undefined, { maximumFractionDigits: 5 })} = {formattedTotal}
            </Tooltip>
          )}
        >
          <span className={classNames({ [errorTextClass]: original.missingLabourEstimate })}>
            {original.missingLabourEstimate && "⚠ "}
            {formattedTotal}
          </span>
        </OverlayTrigger>
      );
    },
  }),

  columnHelper.accessor("purchasingPriority.priority", {
    header: "Purchasing Priority",
    cell: ({ row, table }) => {
      const job = table.options.meta!["job"];
      if (!job) return null;
      return <PurchasingPriorityCell bomNode={row.original} job={job as Job} />;
    },
  }),
];

const MemoizedDescriptionCell = memo(function DescriptionCell({ text }: { text: string | null }) {
  return <TruncatedText className="position-absolute w-100 h-100">{text}</TruncatedText>;
});

const MemoizedTruncatedPreferredVendorCell = memo(function TruncatedPreferredVendorCell({
  item,
}: {
  item: BomNode["item"];
}) {
  return (
    <TruncatedText>
      <PreferredVendorCell item={item} showTax={false} />
    </TruncatedText>
  );
});
